"use client";

import Link from "@/components/Link";
import { useMemo } from "react";
import type { OfferRelation, OfferRelationKind } from "@/lib/offers";
import styles from "./offer-relations.module.css";

const KIND_META: Record<
  OfferRelationKind,
  { icon: string; label: string; pluralLabel: string }
> = {
  region: {
    icon: "fa-solid fa-earth-americas",
    label: "Region",
    pluralLabel: "Regions",
  },
  destination: {
    icon: "fa-solid fa-location-dot",
    label: "Destination",
    pluralLabel: "Destinations",
  },
  package: {
    icon: "fa-solid fa-suitcase-rolling",
    label: "Package",
    pluralLabel: "Packages",
  },
};

const KIND_ORDER: OfferRelationKind[] = ["region", "destination", "package"];

interface OfferRelationsProps {
  relations: OfferRelation[];
  variant?: "card" | "full";
  className?: string;
}

/**
 * Renders an offer's related entities (regions, destinations, packages) as
 * clickable chips. `card` variant lists every entry as a flat row of chips;
 * `full` variant groups entries by kind.
 */
export default function OfferRelations({
  relations,
  variant = "card",
  className,
}: OfferRelationsProps) {
  const grouped = useMemo(() => {
    if (variant !== "full") return null;
    const groups: Record<OfferRelationKind, OfferRelation[]> = {
      region: [],
      destination: [],
      package: [],
    };
    for (const r of relations) groups[r.kind].push(r);
    return groups;
  }, [relations, variant]);

  if (!relations.length) return null;

  if (variant === "full" && grouped) {
    const orderedItems = KIND_ORDER.flatMap((kind) => grouped[kind]);
    return (
      <section
        className={`${styles.full} ${className ?? ""}`.trim()}
        aria-label="Related to this offer"
      >
        <header className={styles.fullHeader}>
          <span className={styles.fullEyebrow}>Explore</span>
          <h4 className={styles.fullTitle}>Related to this offer</h4>
        </header>
        <ul className={styles.tiles} role="list">
          {orderedItems.map((item) => {
            const meta = KIND_META[item.kind];
            return (
              <li key={item.id} className={styles.tileWrap}>
                <Link
                  href={item.href}
                  title={`${meta.label}: ${item.name}`}
                  className={`${styles.tile} ${styles[`tile_${item.kind}`]}`}
                >
                  <span className={styles.tileIcon} aria-hidden="true">
                    <i className={meta.icon} />
                  </span>
                  <span className={styles.tileBody}>
                    <span className={styles.tileKind}>{meta.label}</span>
                    <span className={styles.tileName}>{item.name}</span>
                  </span>
                  <span className={styles.tileArrow} aria-hidden="true">
                    <i className="fa-solid fa-arrow-right" />
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      </section>
    );
  }

  // card variant
  return (
    <div className={`${styles.cardChips} ${className ?? ""}`.trim()}>
      {relations.map((item) => {
        const meta = KIND_META[item.kind];
        return (
          <Link
            key={item.id}
            href={item.href}
            title={`${meta.label}: ${item.name}`}
            className={`${styles.chip} ${styles.chipCompact} ${styles[`chip_${item.kind}`]}`}
          >
            <i
              className={`${meta.icon} ${styles.chipIcon}`}
              aria-hidden="true"
            />
            <span className={styles.chipLabel}>{item.name}</span>
          </Link>
        );
      })}
    </div>
  );
}
